import React, { useState, useContext, useEffect } from "react";
import clsx from "clsx";
import {
  makeStyles,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  MenuItem,
  IconButton,
  Menu,
  Chip,
  Avatar,
  Box,
  Tooltip,
  Switch,
  FormControlLabel
} from "@material-ui/core";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import MenuIcon from "@material-ui/icons/Menu";
import AccountCircle from "@material-ui/icons/AccountCircle";
import FiberManualRecordIcon from "@material-ui/icons/FiberManualRecord";

import MainListItems from "./MainListItems";
import NotificationsPopOver from "../components/NotificationsPopOver";
import UserModal from "../components/UserModal";
import { AuthContext } from "../context/Auth/AuthContext";
import BackdropLoading from "../components/BackdropLoading";
import { i18n } from "../translate/i18n";
import api from "../services/api";
import openSocket from "../services/socket-io";
import { toast } from "react-toastify";

/* ==========================================================================
   CRM LASIT — LAYOUT PRINCIPAL PREMIUM v2.0
   PROHIBIDO MODIFICAR SIN AUTORIZACIÓN  |  Trae AI  |  2026-05-13
   Rediseño completo: sidebar oscuro glassmorphism + AppBar premium.
   Eliminado: botón dark/light mode (no requerido).
   ========================================================================== */

const DRAWER_WIDTH = 240;
const DRAWER_COLLAPSED = 72;

// Paleta de colores del sistema
const C = {
  bg:        "#060A14",
  bgSide:    "#08101E",
  bgCard:    "rgba(255,255,255,0.03)",
  border:    "rgba(0,212,170,0.12)",
  accent:    "#00D4AA",
  accentHov: "#00E8BB",
  text:      "#FFFFFF",
  textMuted: "rgba(255,255,255,0.45)",
  textDim:   "rgba(255,255,255,0.25)",
};

const useStyles = makeStyles((theme) => ({
  "@global": {
    "*": { boxSizing: "border-box" },
    body: {
      background: C.bg,
      fontFamily: "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Ubuntu, sans-serif !important",
    },
    // Scrollbar premium
    "::-webkit-scrollbar":       { width: 6, height: 6 },
    "::-webkit-scrollbar-track": { background: "transparent" },
    "::-webkit-scrollbar-thumb": { background: "rgba(0,212,170,0.25)", borderRadius: 3 },
    "::-webkit-scrollbar-thumb:hover": { background: "rgba(0,212,170,0.5)" },

    // ── Fix texto invisible en fondos blancos hardcodeados ─────────────
    // Los folderPaper de Masivo tienen backgroundColor:'#fff' inline.
    // Forzamos el color a oscuro en esos contextos para mantener legibilidad.
    "[style*='background-color: white'] *":  { color: "#1a1a2e !important" },
    "[style*='background-color: rgb(255, 255, 255)'] *": { color: "#1a1a2e !important" },
    "[style*='backgroundColor:#fff'] *":     { color: "#1a1a2e !important" },
    "[style*='background-color: #fff'] *":   { color: "#1a1a2e !important" },
    "[style*='background-color:#fff'] *":    { color: "#1a1a2e !important" },
    // Fix para fondos claros de carpetas activas (#f1f8e9)
    "[style*='background-color: rgb(241']": { color: "#1a1a2e !important" },
    // Input nativo — solo color del texto, sin forzar fondo oscuro
    "input[type='text'], input[type='email'], input[type='password'], input[type='number']": {
      backgroundColor: "transparent",
      color: "#FFFFFF",
    },
  },

  root: {
    display: "flex",
    height: "100vh",
    background: C.bg,
    overflow: "hidden",
  },

  // ── SIDEBAR ───────────────────────────────────────────────────────────
  // Clases para el CONTENEDOR RAÍZ del Drawer (no solo el Paper interno)
  // Esto hace que el flex layout ajuste el ancho del área de contenido
  drawerOpen: {
    width: DRAWER_WIDTH,
    flexShrink: 0,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: 250,
    }),
  },
  drawerClose: {
    width: DRAWER_COLLAPSED,
    flexShrink: 0,
    overflowX: "hidden",
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: 200,
    }),
  },
  drawerPaper: {
    width: DRAWER_WIDTH,
    background: `linear-gradient(180deg, ${C.bgSide} 0%, ${C.bg} 100%)`,
    borderRight: `1px solid ${C.border}`,
    boxShadow: "4px 0 24px rgba(0,0,0,0.4)",
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: 250,
    }),
    overflowX: "hidden",
    display: "flex",
    flexDirection: "column",
  },
  drawerPaperClose: {
    width: DRAWER_COLLAPSED,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: 200,
    }),
    overflowX: "hidden",
  },

  // Logo del sidebar
  sidebarLogo: {
    display: "flex",
    alignItems: "center",
    padding: "16px 14px",
    borderBottom: `1px solid ${C.border}`,
    minHeight: 64,
    gap: 10,
    overflow: "hidden",
  },
  sidebarLogoImg: {
    width: 38,
    height: 38,
    borderRadius: "50%",
    objectFit: "contain",
    flexShrink: 0,
    border: `1.5px solid rgba(0,212,170,0.4)`,
    background: "rgba(5,15,30,0.6)",
    padding: 3,
  },
  sidebarLogoText: {
    fontWeight: 700,
    fontSize: "1rem",
    color: C.text,
    letterSpacing: "0.5px",
    whiteSpace: "nowrap",
    "& span": { color: C.accent },
  },

  // Botón de cerrar/abrir drawer
  toggleBtn: {
    marginLeft: "auto",
    color: C.textMuted,
    padding: 4,
    "&:hover": { color: C.accent, background: "rgba(0,212,170,0.08)" },
  },

  // ── APPBAR ────────────────────────────────────────────────────────────
  appBar: {
    zIndex: theme.zIndex.drawer + 1,
    background: `linear-gradient(135deg, rgba(6,10,20,0.97) 0%, rgba(8,16,30,0.97) 100%)`,
    backdropFilter: "blur(12px)",
    WebkitBackdropFilter: "blur(12px)",
    borderBottom: `1px solid ${C.border}`,
    boxShadow: "0 2px 20px rgba(0,0,0,0.4)",
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: 250,
    }),
  },
  appBarShift: {
    marginLeft: DRAWER_WIDTH,
    width: `calc(100% - ${DRAWER_WIDTH}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: 250,
    }),
  },

  toolbar: {
    paddingLeft: 16,
    paddingRight: 20,
    minHeight: 64,
    display: "flex",
    alignItems: "center",
    gap: 8,
  },

  menuButton: {
    color: C.textMuted,
    "&:hover": { color: C.accent, background: "rgba(0,212,170,0.08)" },
    marginRight: 4,
  },
  menuButtonHidden: { display: "none" },

  // Título de la página actual
  pageTitle: {
    flexGrow: 1,
    fontWeight: 600,
    fontSize: "1.05rem",
    color: C.textMuted,
    letterSpacing: "0.3px",
  },

  // Chip de estado Gemini
  statusChip: {
    height: 26,
    fontWeight: 600,
    fontSize: "0.72rem",
    letterSpacing: "0.5px",
    borderRadius: 13,
    border: "1px solid",
    "& .MuiChip-label": { padding: "0 10px" },
  },
  chipConnected: {
    background: "rgba(0,212,170,0.12)",
    color: C.accent,
    borderColor: "rgba(0,212,170,0.35)",
  },
  chipDisconnected: {
    background: "rgba(220,50,50,0.12)",
    color: "#FF6B6B",
    borderColor: "rgba(220,50,50,0.35)",
  },

  // Separador vertical
  vDivider: {
    width: 1,
    height: 28,
    background: C.border,
    margin: "0 8px",
    flexShrink: 0,
  },

  // Notificaciones
  notifBtn: {
    color: C.textMuted,
    "&:hover": { color: C.accent, background: "rgba(0,212,170,0.08)" },
  },

  // Info del usuario
  userBlock: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    marginRight: 10,
  },
  userName: {
    fontWeight: 700,
    fontSize: "0.88rem",
    color: C.text,
    lineHeight: 1.2,
    whiteSpace: "nowrap",
  },
  userTimer: {
    fontSize: "0.72rem",
    color: C.accent,
    lineHeight: 1.2,
    letterSpacing: "0.3px",
  },

  // Avatar del usuario
  userAvatar: {
    width: 38,
    height: 38,
    cursor: "pointer",
    border: `2px solid rgba(0,212,170,0.4)`,
    transition: "border-color 0.2s",
    "&:hover": { borderColor: C.accent },
  },
  iconAvatar: {
    color: C.textMuted,
    "&:hover": { color: C.accent, background: "rgba(0,212,170,0.08)" },
  },

  // Dot de conexión en avatar
  onlineDot: {
    position: "relative",
    "& .dot": {
      position: "absolute",
      bottom: 0,
      right: 0,
      width: 10,
      height: 10,
      borderRadius: "50%",
      background: C.accent,
      border: `2px solid ${C.bg}`,
    },
  },

  // Menú desplegable del usuario
  userMenu: {
    "& .MuiPaper-root": {
      background: "#0D1B2A",
      border: `1px solid ${C.border}`,
      boxShadow: "0 16px 40px rgba(0,0,0,0.6)",
      borderRadius: 12,
      minWidth: 180,
      marginTop: 8,
    },
    "& .MuiMenuItem-root": {
      color: C.textMuted,
      fontSize: "0.88rem",
      padding: "10px 18px",
      "&:hover": { background: "rgba(0,212,170,0.08)", color: C.text },
    },
  },

  // ── CONTENIDO PRINCIPAL ───────────────────────────────────────────────
  appBarSpacer: { minHeight: 64 },

  content: {
    flex: 1,
    overflow: "auto",
    background: C.bg,
    display: "flex",
    flexDirection: "column",
  },
}));

// ─── Utilidad para formatear tiempo de sesión ───────────────────────────────
const formatTime = (seconds) => {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

// ─── Componente Principal ────────────────────────────────────────────────────
const LoggedInLayout = ({ children }) => {
  const classes = useStyles();
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const authContext = useContext(AuthContext);
  const { handleLogout, loading } = authContext || {};
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [drawerVariant, setDrawerVariant] = useState("permanent");
  const user = authContext?.user || {};
  const [geminiConnected, setGeminiConnected] = useState(false);
  const [geminiEnabled, setGeminiEnabled] = useState(true);
  const [sessionSeconds, setSessionSeconds] = useState(0);

  // Timer de sesión
  useEffect(() => {
    const timer = setInterval(() => setSessionSeconds((p) => p + 1), 1000);
    return () => clearInterval(timer);
  }, []);

  // Estado Gemini y Switch
  useEffect(() => {
    api.get("/settings/gemini-check")
      .then(({ data }) => setGeminiConnected(data.isConnected))
      .catch(() => {});
      
    api.get("/settings")
      .then(({ data }) => {
        const enabledSetting = data.find(s => s.key === "geminiEnabled");
        if (enabledSetting) {
          setGeminiEnabled(enabledSetting.value === "enabled");
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const socket = openSocket();
    socket.on("settings", (data) => {
      if (data.action === "update" && data.setting.key === "geminiApiKey") {
        api.get("/settings/gemini-check")
          .then(({ data }) => setGeminiConnected(data.isConnected))
          .catch(() => {});
      }
      if (data.action === "update" && data.setting.key === "geminiEnabled") {
        setGeminiEnabled(data.setting.value === "enabled");
      }
    });
    return () => socket.disconnect();
  }, []);

  const handleToggleGeminiEnabled = async (e) => {
    const newValue = e.target.checked;
    setGeminiEnabled(newValue);
    try {
      await api.put("/settings/geminiEnabled", { value: newValue ? "enabled" : "disabled" });
      toast.success(`Agente IA ${newValue ? "Activado" : "Apagado"} globalmente`);
    } catch (err) {
      setGeminiEnabled(!newValue);
      toast.error("Error al cambiar el estado del agente IA");
    }
  };

  // Responsive: drawer temporal en móvil
  useEffect(() => {
    if (document.body.offsetWidth < 600) {
      setDrawerVariant("temporary");
      setDrawerOpen(false);
    }
  }, []);

  const handleMenu = (e) => { setAnchorEl(e.currentTarget); setMenuOpen(true); };
  const handleCloseMenu = () => { setAnchorEl(null); setMenuOpen(false); };
  const handleOpenUserModal = () => { setUserModalOpen(true); handleCloseMenu(); };
  const handleClickLogout = () => { handleCloseMenu(); handleLogout(); };
  const drawerClose = () => { if (document.body.offsetWidth < 600) setDrawerOpen(false); };

  if (loading) return <BackdropLoading />;

  return (
    <div className={classes.root}>

      {/* ── SIDEBAR ─────────────────────────────────────────────── */}
      <Drawer
        variant={drawerVariant}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        className={clsx(drawerOpen ? classes.drawerOpen : classes.drawerClose)}
        classes={{
          paper: clsx(classes.drawerPaper, !drawerOpen && classes.drawerPaperClose),
        }}
      >
        {/* Logo / Cabecera del sidebar */}
        <div className={classes.sidebarLogo}>
          <img src="/logo.png" alt="CRM LASIT" className={classes.sidebarLogoImg} />
          {drawerOpen && (
            <span className={classes.sidebarLogoText}>
              CRM <span>LASIT</span>
            </span>
          )}
          <IconButton
            size="small"
            className={classes.toggleBtn}
            onClick={() => setDrawerOpen(!drawerOpen)}
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
        </div>

        {/* Menú de navegación */}
        <List style={{ flex: 1, paddingTop: 8 }}>
          <MainListItems drawerClose={drawerClose} drawerOpen={drawerOpen} />
        </List>
      </Drawer>

      <UserModal
        open={userModalOpen}
        onClose={() => setUserModalOpen(false)}
        userId={user?.id}
      />

      {/* ── APPBAR ──────────────────────────────────────────────── */}
      <AppBar
        position="absolute"
        className={clsx(classes.appBar, drawerOpen && classes.appBarShift)}
        elevation={0}
      >
        <Toolbar className={classes.toolbar} disableGutters>

          {/* Botón abrir menú (visible cuando drawer está cerrado) */}
          <IconButton
            edge="start"
            onClick={() => setDrawerOpen(!drawerOpen)}
            className={clsx(classes.menuButton, drawerOpen && classes.menuButtonHidden)}
            size="small"
          >
            <MenuIcon />
          </IconButton>

          {/* Título */}
          <Typography component="h1" className={classes.pageTitle} noWrap>
            CRM <span style={{ color: "#00D4AA", fontWeight: 800 }}>LASIT</span>
          </Typography>

          {/* Estado IA (Gemini) y Toggle */}
          <Box display="flex" alignItems="center" gap={1}>
            {user.profile === "admin" && (
              <Tooltip title="Apagar o encender el Agente IA globalmente">
                <FormControlLabel
                  control={
                    <Switch
                      checked={geminiEnabled}
                      onChange={handleToggleGeminiEnabled}
                      color="primary"
                      size="small"
                    />
                  }
                  label={<Typography variant="caption" style={{ color: C.textMuted, fontWeight: 600 }}>{geminiEnabled ? "ON" : "OFF"}</Typography>}
                  labelPlacement="start"
                  style={{ margin: 0, marginRight: 8 }}
                />
              </Tooltip>
            )}
            <Chip
              icon={
                <FiberManualRecordIcon
                  style={{ fontSize: 9, color: (geminiConnected && geminiEnabled) ? "#00D4AA" : "#FF6B6B" }}
                />
              }
              label={(geminiConnected && geminiEnabled) ? "IA Conectada" : (!geminiEnabled ? "IA Apagada" : "IA Desconectada")}
              className={clsx(
                classes.statusChip,
                (geminiConnected && geminiEnabled) ? classes.chipConnected : classes.chipDisconnected
              )}
            />
          </Box>

          <div className={classes.vDivider} />

          {/* Notificaciones */}
          {user.id && (
            <div className={classes.notifBtn}>
              <NotificationsPopOver />
            </div>
          )}

          <div className={classes.vDivider} />

          {/* Info del usuario */}
          <div className={classes.userBlock}>
            <span className={classes.userName}>¡Hola, {user.name}!</span>
            <span className={classes.userTimer}>En línea: {formatTime(sessionSeconds)}</span>
          </div>

          {/* Avatar / Menú usuario */}
          <div className={classes.onlineDot}>
            {user.profileImage ? (
              <Avatar
                src={user.profileImage}
                alt={user.name}
                className={classes.userAvatar}
                onClick={handleMenu}
              />
            ) : (
              <Tooltip title={user.name || "Mi perfil"}>
                <IconButton
                  aria-label="cuenta"
                  aria-controls="menu-appbar"
                  aria-haspopup="true"
                  onClick={handleMenu}
                  className={classes.iconAvatar}
                  size="small"
                >
                  <AccountCircle style={{ fontSize: 36 }} />
                </IconButton>
              </Tooltip>
            )}
            <span className="dot" />
          </div>

          <Menu
            id="menu-appbar"
            anchorEl={anchorEl}
            getContentAnchorEl={null}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            open={menuOpen}
            onClose={handleCloseMenu}
            className={classes.userMenu}
          >
            {user.profile === "admin" && (
              <MenuItem onClick={handleOpenUserModal}>
                {i18n.t("mainDrawer.appBar.user.profile")}
              </MenuItem>
            )}
            <MenuItem onClick={handleClickLogout}>
              {i18n.t("mainDrawer.appBar.user.logout")}
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* ── CONTENIDO ───────────────────────────────────────────── */}
      <main className={classes.content}>
        <div className={classes.appBarSpacer} />
        {children ?? null}
      </main>
    </div>
  );
};

export default LoggedInLayout;
